import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "./api/axios";
import { useAuth } from "./contexts/AuthContext";
import { getImageUrl } from "./utils/image";
import { useActivityLogger } from "./hooks/useActivity";

export default function Profile() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  // State
  const [patients, setPatients] = useState([]); // Khusus Pendamping
  const [caregivers, setCaregivers] = useState([]); // Khusus Pasien
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  
  const { logUserActivity, loading: userLoading } = useActivityLogger();

  // Cek Role
  const isPendamping = user?.roles?.some((r) => {
    const roleName = r.name || r.Name || "";
    return roleName.toLowerCase() === "pendamping";
  });

  // Jika bukan pendamping, kita anggap pasien
  const isPasien = !isPendamping;

  useEffect(() => {
    if (!userLoading) {
      logUserActivity(
        "user",
        "view_profile",
        "Profile",
        "User viewed profile page"
      );
    }
  }, [userLoading]);

  // 1. Fetch daftar PASIEN (jika user = pendamping)
  useEffect(() => {
    if (isPendamping) {
      const fetchPatients = async () => {
        try {
          const response = await api.get("/caregiver/patients");
          setPatients(response.data || []);
        } catch (error) {
          console.error("Gagal memuat daftar pasien:", error);
        }
      };
      fetchPatients();
    }
  }, [isPendamping]);

  // 2. Fetch daftar PENDAMPING (jika user = pasien)
  useEffect(() => {
    if (isPasien) {
      const fetchCaregivers = async () => {
        try {
          const response = await api.get("/patient/caregivers");
          setCaregivers(response.data || []);
        } catch (error) {
          console.error("Gagal memuat daftar pendamping:", error);
        }
      };
      fetchCaregivers();
    }
  }, [isPasien]);

  const handleLogout = async () => {
    try {
      logUserActivity(
        "user",
        "logout",
        "Profile",
        "User logged out from profile page"
      );
      await api.post("/logout", null, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      setUser(null);
      navigate("/");
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const formatPhone = (phone) => phone || "-";

  return (
    <div className="min-h-screen bg-white px-4 py-4 pb-32">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => {
            logUserActivity(
              "user",
              "go_back_from_profile",
              "Profile",
              "User navigated back from profile page"
            );
            navigate("/");
          }}
        >
          <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
        </button>
        <div className="flex gap-3">
          <button onClick={() => navigate("/edit-profile")}>
            <img src="/icon-edit.png" alt="Edit" className="h-6 w-6" />
          </button>
          <button onClick={() => setShowLogoutModal(true)}>
            <img src="/icon-logout.png" alt="Logout" className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Profile Picture */}
      <div className="flex flex-col items-center mb-6">
        <img
          src={user?.image_url ? getImageUrl(user.image_url) : "/avatar.png"}
          alt="Profile"
          className="h-24 w-24 rounded-full object-cover mb-2"
        />
        <h2 className="text-lg font-semibold">{user?.nama_ktp || "-"}</h2>
        <p className="text-gray-500 text-sm">{user?.username || "-"}</p>
        
        {/* Badge Role */}
        {isPendamping && (
          <span className="mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
            Pendamping
          </span>
        )}
      </div>

      {/* Profile Form */}
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-1 block">NIK</label>
          <input
            className="w-full border rounded-lg px-3 py-2 bg-gray-100"
            value={user?.nik || "-"}
            disabled
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">Nama Samaran</label>
          <input
            className="w-full border rounded-lg px-3 py-2 bg-gray-100"
            value={user?.nama_samaran || "-"}
            disabled
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">No. HP</label>
          <input
            className="w-full border rounded-lg px-3 py-2 bg-gray-100"
            value={formatPhone(user?.nomor_hp)}
            disabled
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">E-mail</label>
          <input
            className="w-full border rounded-lg px-3 py-2 bg-gray-100"
            value={user?.email || "-"}
            disabled
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">Preferensi</label>
          {user?.preferences?.length > 0 ? (
            <ul className="list-disc pl-6 text-sm text-gray-800 bg-gray-50 p-2 rounded-lg">
              {user.preferences.map((pref, i) => (
                <li key={i}>{pref}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 bg-gray-50 p-2 rounded-lg">
              Tidak ada preferensi
            </p>
          )}
        </div>

        {/* SECTION: DAFTAR PASIEN (Hanya untuk Pendamping) */}
        {isPendamping && (
          <div className="pt-2 border-t mt-4">
            <h3 className="text-md font-bold mb-3 text-blue-800">
              Daftar Pasien Anda
            </h3>
            {patients.length > 0 ? (
              <div className="grid gap-3">
                {patients.map((p) => (
                  <div
                    key={p.ID}
                    className="border rounded-lg p-3 bg-blue-50/50 flex justify-between items-center"
                  >
                    <div>
                      <p className="font-semibold text-sm text-gray-900">
                        {p.nama_ktp}
                      </p>
                      <p className="text-xs text-gray-500">@{p.username}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xs bg-white border px-2 py-1 rounded text-gray-600">
                        ID: {p.ID}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed">
                <p className="text-sm text-gray-500 italic">
                  Belum ada pasien terhubung.
                </p>
                <button
                  onClick={() => navigate("/edit-profile")}
                  className="mt-2 text-xs text-blue-600 hover:underline"
                >
                  Kelola di Edit Profil
                </button>
              </div>
            )}
          </div>
        )}

        {/* SECTION: DAFTAR PENDAMPING (Hanya untuk Pasien) */}
        {isPasien && (
          <div className="pt-2 border-t mt-4">
            <h3 className="text-md font-bold mb-3 text-green-800">
              Pendamping Anda
            </h3>
            {caregivers.length > 0 ? (
              <div className="grid gap-3">
                {caregivers.map((c) => (
                  <div
                    key={c.ID}
                    className="border rounded-lg p-3 bg-green-50/50 flex justify-between items-center"
                  >
                    <div>
                      <p className="font-semibold text-sm text-gray-900">
                        {c.nama_ktp}
                      </p>
                      <p className="text-xs text-gray-500">@{c.username}</p>
                      {c.nomor_hp && (
                        <p className="text-xs text-gray-500 mt-1">
                          HP: {c.nomor_hp}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed">
                <p className="text-sm text-gray-500 italic">
                  Anda belum memiliki pendamping.
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Minta pendamping Anda untuk menambahkan ID User Anda ({user?.ID}).
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Logout Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm w-full text-center mx-4">
            <h3 className="text-lg font-semibold mb-4">Keluar dari akun?</h3>
            <p className="text-sm text-gray-700 mb-6">
              Apakah Anda yakin ingin keluar?
            </p>
            <div className="flex justify-between">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="text-gray-500 hover:underline"
              >
                Batal
              </button>
              <button
                onClick={handleLogout}
                className="text-red-600 hover:underline font-medium"
              >
                Ya, Keluar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}